import React, { useState, useMemo, useCallback } from "react";
import { rasService } from "../services/rasService";
import { LookupRecord, LookupInsertData, QueryParams, ApiResponse } from "../types/api";
import StableSelect from "./StableSelect";
import UncontrolledInput from "./UncontrolledInput";

// Advanced filter definitions for Lookup Data Management
const ADV_COLUMNS = {
    origin: { label: "Origin", type: "text" as const },
    type: { label: "Type", type: "text" as const },
    external_id: { label: "External ID", type: "text" as const },
    internal_id: { label: "Internal ID", type: "text" as const },
    match_type: { label: "Match Type", type: "text" as const },
    created_at: { label: "Created At", type: "date" as const },
} as const;
type ColumnKey = keyof typeof ADV_COLUMNS;
type ColumnType = (typeof ADV_COLUMNS)[ColumnKey]["type"];
interface FilterRow {
    id: string;
    column: ColumnKey;
    operator: string;
    value: string;
}
const OPERATORS_BY_TYPE: Record<ColumnType, Array<{ value: string; label: string }>> = {
    text: [
        { value: "=", label: "Equals" },
        { value: "!=", label: "Not Equals" },
        { value: "LIKE", label: "Contains" },
        // { value: "ILIKE", label: "Contains (CI)" },
    ],
    date: [
        { value: "=", label: "On Date" },
        { value: "!=", label: "Not On Date" },
        { value: ">", label: "After" },
        { value: ">=", label: "On or After" },
        { value: "<", label: "Before" },
        { value: "<=", label: "On or Before" },
    ],
};

const LookupManagementPanel: React.FC = () => {
    const [activeTab, setActiveTab] = useState<"search" | "edit">("search");

    return (
        <div className="panel-content">
            {/* Tab Navigation */}
            <div style={{ display: "flex", gap: "4px", marginBottom: "30px", borderBottom: "2px solid #e9ecef" }}>
                <button
                    onClick={() => setActiveTab("search")}
                    style={{
                        padding: "12px 24px",
                        background: activeTab === "search" ? "#e3f2fd" : "transparent",
                        border: "none",
                        color: activeTab === "search" ? "#007bff" : "#6c757d",
                        borderBottom: activeTab === "search" ? "3px solid #007bff" : "3px solid transparent",
                        cursor: "pointer",
                        fontWeight: "500",
                    }}
                >
                    Search & Browse
                </button>
                <button
                    onClick={() => setActiveTab("edit")}
                    style={{
                        padding: "12px 24px",
                        background: activeTab === "edit" ? "#e3f2fd" : "transparent",
                        border: "none",
                        color: activeTab === "edit" ? "#007bff" : "#6c757d",
                        borderBottom: activeTab === "edit" ? "3px solid #007bff" : "3px solid transparent",
                        cursor: "pointer",
                        fontWeight: "500",
                    }}
                >
                    Edit Records
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === "search" && <LookupSearchTab />}
            {activeTab === "edit" && <LookupEditTab />}
        </div>
    );
};

const LookupSearchTab: React.FC = () => {
    // State for lookup advanced filters
    const [filters, setFilters] = useState<FilterRow[]>([{ id: crypto.randomUUID(), column: "origin", operator: "=", value: "" }]);
    const [limit, setLimit] = useState<number>(25);
    const [results, setResults] = useState<ApiResponse<LookupRecord[]> | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Add/remove/update filter utilities
    const columnOptions = useMemo(() => Object.entries(ADV_COLUMNS).map(([key, cfg]) => ({ value: key, label: cfg.label })), []);
    const getOperatorOptions = useCallback((col: ColumnKey) => {
        const t = ADV_COLUMNS[col].type as ColumnType;
        return OPERATORS_BY_TYPE[t] || OPERATORS_BY_TYPE.text;
    }, []);
    const addFilter = useCallback(() => {
        setFilters((prev) => [...prev, { id: crypto.randomUUID(), column: "origin", operator: "=", value: "" }]);
    }, []);
    const removeFilter = useCallback((id: string) => {
        setFilters((prev) => {
            const next = prev.filter((f) => f.id !== id);
            return next.length ? next : [{ id: crypto.randomUUID(), column: "origin", operator: "=", value: "" }];
        });
    }, []);
    const updateFilter = useCallback(
        (id: string, field: keyof FilterRow, val: string) => {
            setFilters((prev) =>
                prev.map((f) => {
                    if (f.id !== id) return f;
                    const updated = { ...f, [field]: val };
                    if (field === "column") {
                        const ops = getOperatorOptions(val as ColumnKey);
                        updated.operator = ops[0]?.value || "=";
                    }
                    return updated;
                })
            );
        },
        [getOperatorOptions]
    );
    const clearFilters = useCallback(() => {
        setFilters([{ id: crypto.randomUUID(), column: "origin", operator: "=", value: "" }]);
        setResults(null);
        setError(null);
    }, []);
    // Perform search
    const handleSearch = async () => {
        setLoading(true);
        setError(null);
        try {
            const qp: QueryParams = { limit };
            filters.forEach((f) => {
                if (f.value.trim()) {
                    const key = f.operator === "=" ? f.column : `${f.column}${f.operator}`;
                    let v = f.value.trim();
                    if (f.operator === "LIKE" || f.operator === "ILIKE") v = `%${v}%`;
                    (qp as any)[key] = v;
                }
            });
            const result = await rasService.queryLookup(qp);
            setResults(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to search lookup records");
            setResults(null);
        } finally {
            setLoading(false);
        }
    };

    // Export lookup records to CSV
    const exportToCSV = useCallback(() => {
        if (!results?.data?.length) return;
        const rows = results.data;
        const headers = Object.keys(rows[0]).join(",");
        const csvBody = rows
            .map((record) =>
                Object.values(record)
                    .map((val) => {
                        const s = String(val ?? "");
                        return s.includes(",") ? `"${s.replace(/"/g, '""')}"` : s;
                    })
                    .join(",")
            )
            .join("\n");
        const blob = new Blob([`${headers}\n${csvBody}`], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `lookup_records_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [results]);

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <div>
            {error && (
                <div className="error-message">
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Advanced Filters */}
            <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                    <h3>Advanced Filters</h3>
                    <div style={{ display: "flex", gap: "10px" }}>
                        <button
                            onClick={addFilter}
                            style={{
                                padding: "6px 12px",
                                border: "1px solid #007bff",
                                borderRadius: "4px",
                                backgroundColor: "#007bff",
                                color: "white",
                                cursor: "pointer",
                            }}
                        >
                            + Add Filter
                        </button>
                        <button
                            onClick={clearFilters}
                            style={{
                                padding: "6px 12px",
                                border: "1px solid #6c757d",
                                borderRadius: "4px",
                                backgroundColor: "#6c757d",
                                color: "white",
                                cursor: "pointer",
                            }}
                        >
                            Clear All
                        </button>
                    </div>
                </div>
                {filters.map((filter, index) => (
                    <div
                        key={filter.id}
                        style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                            marginBottom: "10px",
                            padding: "10px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            backgroundColor: "#f9f9f9",
                        }}
                    >
                        <span style={{ minWidth: "30px", color: "#666" }}>#{index + 1}</span>
                        <div style={{ minWidth: "200px" }}>
                            <StableSelect
                                id={`column-${filter.id}`}
                                value={filter.column}
                                onChange={(v) => updateFilter(filter.id, "column", v as string)}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                            >
                                {columnOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </StableSelect>
                        </div>
                        <div style={{ minWidth: "150px" }}>
                            <StableSelect
                                id={`operator-${filter.id}`}
                                value={filter.operator}
                                onChange={(v) => updateFilter(filter.id, "operator", v as string)}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                            >
                                {getOperatorOptions(filter.column).map((op) => (
                                    <option key={op.value} value={op.value}>
                                        {op.label}
                                    </option>
                                ))}
                            </StableSelect>
                        </div>
                        <div style={{ flex: 1, minWidth: "200px" }}>
                            <UncontrolledInput
                                id={`value-${filter.id}`}
                                type={ADV_COLUMNS[filter.column].type === "date" ? "date" : "text"}
                                value={filter.value}
                                onChange={(v) => updateFilter(filter.id, "value", v)}
                                placeholder={`Enter ${ADV_COLUMNS[filter.column].label.toLowerCase()}...`}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                            />
                        </div>
                        <div style={{ flexShrink: 0, marginLeft: "16px" }}>
                            <button
                                onClick={() => removeFilter(filter.id)}
                                style={{
                                    padding: "6px 10px",
                                    border: "1px solid #dc3545",
                                    borderRadius: "4px",
                                    backgroundColor: "#dc3545",
                                    color: "white",
                                    cursor: "pointer",
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            {/* Search Controls */}
            <div
                style={{
                    display: "flex",
                    gap: "15px",
                    alignItems: "center",
                    marginBottom: "20px",
                    padding: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#f8f9fa",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <label htmlFor="lookup-limit" style={{ marginRight: "8px", fontWeight: "bold" }}>
                        Limit:
                    </label>
                    <select
                        id="lookup-limit"
                        value={limit}
                        onChange={(e) => setLimit(parseInt(e.target.value) || 0)}
                        style={{ padding: "6px", border: "1px solid #ddd", borderRadius: "4px" }}
                    >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={600}>600</option>
                    </select>
                </div>
                <button
                    onClick={handleSearch}
                    disabled={loading}
                    style={{
                        padding: "10px 20px",
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontWeight: "bold",
                        opacity: loading ? 0.6 : 1,
                    }}
                >
                    {loading ? (
                        <>
                            {" "}
                            <span className="loading-spinner" style={{ marginRight: "8px" }} /> Searching...
                        </>
                    ) : (
                        "Search"
                    )}
                </button>
                {results?.data && results.data.length > 0 && (
                    <button
                        onClick={exportToCSV}
                        style={{
                            padding: "10px 16px",
                            border: "1px solid #17a2b8",
                            borderRadius: "4px",
                            backgroundColor: "#17a2b8",
                            color: "white",
                            cursor: "pointer",
                        }}
                    >
                        Export CSV
                    </button>
                )}
            </div>

            {/* Results */}
            {results && (
                <div style={{ border: "1px solid #e9ecef", borderRadius: "8px", backgroundColor: "#ffffff", overflow: "hidden" }}>
                    <div style={{ padding: "15px", backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}>
                        <h3 style={{ margin: 0 }}>Lookup Records</h3>
                        <p style={{ margin: "5px 0 0 0", color: "#6c757d" }}>
                            Found {results.count || 0} records {results.data && `(showing ${results.data.length})`}
                        </p>
                    </div>

                    {results.data && results.data.length > 0 ? (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px", fontSize: "12px" }}>
                                <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0 }}>
                                    <tr>
                                        <th
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "left",
                                                borderBottom: "1px solid #e9ecef",
                                                backgroundColor: "#f8f9fa",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                userSelect: "none",
                                            }}
                                        >
                                            ID
                                        </th>
                                        <th
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "left",
                                                borderBottom: "1px solid #e9ecef",
                                                backgroundColor: "#f8f9fa",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                userSelect: "none",
                                            }}
                                        >
                                            Origin
                                        </th>
                                        <th
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "left",
                                                borderBottom: "1px solid #e9ecef",
                                                backgroundColor: "#f8f9fa",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                userSelect: "none",
                                            }}
                                        >
                                            Type
                                        </th>
                                        <th
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "left",
                                                borderBottom: "1px solid #e9ecef",
                                                backgroundColor: "#f8f9fa",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                userSelect: "none",
                                            }}
                                        >
                                            External ID
                                        </th>
                                        <th
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "left",
                                                borderBottom: "1px solid #e9ecef",
                                                backgroundColor: "#f8f9fa",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                userSelect: "none",
                                            }}
                                        >
                                            Internal ID
                                        </th>
                                        <th
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "left",
                                                borderBottom: "1px solid #e9ecef",
                                                backgroundColor: "#f8f9fa",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                userSelect: "none",
                                            }}
                                        >
                                            Match Type
                                        </th>
                                        <th
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "left",
                                                borderBottom: "1px solid #e9ecef",
                                                backgroundColor: "#f8f9fa",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                userSelect: "none",
                                            }}
                                        >
                                            Created At
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.data.map((record, index) => (
                                        <tr key={record.id} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa" }}>
                                            <td
                                                style={{
                                                    padding: "8px",
                                                    borderBottom: "1px solid #e9ecef",
                                                    fontSize: "12px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {record.id}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "8px",
                                                    borderBottom: "1px solid #e9ecef",
                                                    fontSize: "12px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        backgroundColor: "#007bff",
                                                        color: "white",
                                                        padding: "2px 8px",
                                                        borderRadius: "12px",
                                                        fontSize: "0.8rem",
                                                        fontWeight: "500",
                                                    }}
                                                >
                                                    {record.origin}
                                                </span>
                                            </td>
                                            <td
                                                style={{
                                                    padding: "8px",
                                                    borderBottom: "1px solid #e9ecef",
                                                    fontSize: "12px",
                                                    textTransform: "capitalize",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {record.type}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "8px",
                                                    borderBottom: "1px solid #e9ecef",
                                                    fontSize: "12px",
                                                    fontFamily: "monospace",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {record.external_id}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "8px",
                                                    borderBottom: "1px solid #e9ecef",
                                                    fontSize: "12px",
                                                    fontFamily: "monospace",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {record.internal_id || "-"}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "8px",
                                                    borderBottom: "1px solid #e9ecef",
                                                    fontSize: "12px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {record.match_type}
                                            </td>
                                            <td
                                                style={{
                                                    padding: "8px",
                                                    borderBottom: "1px solid #e9ecef",
                                                    fontSize: "12px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {formatDate(record.created_at)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{ padding: "40px", textAlign: "center", color: "#6c757d" }}>No lookup records found matching your criteria</div>
                    )}
                </div>
            )}
        </div>
    );
};

const LookupEditTab: React.FC = () => {
    // Insert form state
    const [insertFormData, setInsertFormData] = useState<LookupInsertData>({
        origin: "RAS",
        type: "horse",
        external_id: "",
        internal_id: "",
        match_type: "",
    });
    const [insertLoading, setInsertLoading] = useState(false);
    const [insertMessage, setInsertMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    // Delete form state
    const [deleteId, setDeleteId] = useState("");
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    // Insert handlers
    const handleInsertSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!insertFormData.external_id.trim()) {
            setInsertMessage({ type: "error", text: "External ID is required" });
            return;
        }

        setInsertLoading(true);

        try {
            const result = await rasService.insertLookup(insertFormData);
            setInsertMessage({ type: "success", text: `Success! Created record with ID: ${result.data?.id}` });
            setInsertFormData({ ...insertFormData, external_id: "", internal_id: "" });
            setTimeout(() => setInsertMessage(null), 5000);
        } catch (err) {
            setInsertMessage({ type: "error", text: err instanceof Error ? err.message : "Failed to create lookup record" });
            setTimeout(() => setInsertMessage(null), 5000);
        } finally {
            setInsertLoading(false);
        }
    };

    // Delete handlers
    const handleDelete = async () => {
        if (!deleteId.trim()) {
            setDeleteMessage({ type: "error", text: "Please enter a lookup record ID" });
            return;
        }

        if (!window.confirm(`Are you sure you want to delete lookup record ${deleteId}? This action cannot be undone.`)) {
            return;
        }

        setDeleteLoading(true);

        try {
            const result = await rasService.deleteLookup(parseInt(deleteId));
            setDeleteMessage({ type: "success", text: `Success! Deleted record: ${result.data?.external_id}` });
            setDeleteId("");
            setTimeout(() => setDeleteMessage(null), 5000);
        } catch (err) {
            setDeleteMessage({ type: "error", text: err instanceof Error ? err.message : "Failed to delete lookup record" });
            setTimeout(() => setDeleteMessage(null), 5000);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>
            {/* Insert New Record Section */}
            <div style={{ flex: "1", minWidth: "500px" }}>
                {insertMessage && <div className={insertMessage.type === "success" ? "success-message" : "error-message"}>{insertMessage.text}</div>}

                <form onSubmit={handleInsertSubmit}>
                    <div style={{ padding: "20px", border: "1px solid #e9ecef", borderRadius: "8px", backgroundColor: "#f8f9fa" }}>
                        <h3 style={{ margin: "0 0 20px 0", color: "#495057" }}>Insert New Lookup Record</h3>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px" }}>
                            <div>
                                <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>Origin *:</label>
                                <select
                                    value={insertFormData.origin}
                                    onChange={(e) => setInsertFormData({ ...insertFormData, origin: e.target.value as LookupInsertData["origin"] })}
                                    required
                                    style={{ width: "100%", padding: "8px", border: "1px solid #ced4da", borderRadius: "4px" }}
                                >
                                    <option value="RAS">RAS</option>
                                    <option value="MD">MD</option>
                                    <option value="HRN">HRN</option>
                                    <option value="JAR">JAR</option>
                                    <option value="SP">SP</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>Type *:</label>
                                <select
                                    value={insertFormData.type}
                                    onChange={(e) => setInsertFormData({ ...insertFormData, type: e.target.value as LookupInsertData["type"] })}
                                    required
                                    style={{ width: "100%", padding: "8px", border: "1px solid #ced4da", borderRadius: "4px" }}
                                >
                                    <option value="horse">Horse</option>
                                    <option value="race">Race</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>External ID *:</label>
                                <input
                                    type="text"
                                    placeholder="External system ID"
                                    value={insertFormData.external_id}
                                    onChange={(e) => setInsertFormData({ ...insertFormData, external_id: e.target.value })}
                                    required
                                    style={{ width: "100%", padding: "8px", border: "1px solid #ced4da", borderRadius: "4px" }}
                                />
                            </div>

                            <div>
                                <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>Internal ID:</label>
                                <input
                                    type="text"
                                    placeholder="Internal system ID (optional)"
                                    value={insertFormData.internal_id}
                                    onChange={(e) => setInsertFormData({ ...insertFormData, internal_id: e.target.value })}
                                    style={{ width: "100%", padding: "8px", border: "1px solid #ced4da", borderRadius: "4px" }}
                                />
                            </div>

                            <div style={{ gridColumn: "span 2" }}>
                                <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>Match Type:</label>
                                <input
                                    type="text"
                                    placeholder="Match type (defaults to MANUAL_MATCH)"
                                    value={insertFormData.match_type}
                                    onChange={(e) => setInsertFormData({ ...insertFormData, match_type: e.target.value })}
                                    style={{ width: "100%", padding: "8px", border: "1px solid #ced4da", borderRadius: "4px" }}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={insertLoading}
                            style={{
                                marginTop: "20px",
                                padding: "12px 24px",
                                backgroundColor: "#28a745",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: insertLoading ? "not-allowed" : "pointer",
                                fontSize: "1rem",
                                fontWeight: "500",
                                opacity: insertLoading ? 0.6 : 1,
                            }}
                        >
                            {insertLoading ? (
                                <>
                                    <span className="loading-spinner" />
                                    Creating...
                                </>
                            ) : (
                                "Create Lookup Record"
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Delete Record Section */}
            <div style={{ flex: "0 0 400px", minWidth: "500px" }}>
                {deleteMessage && <div className={deleteMessage.type === "success" ? "success-message" : "error-message"}>{deleteMessage.text}</div>}

                <div style={{ padding: "20px", border: "1px solid #e9ecef", borderRadius: "8px", backgroundColor: "#fff5f5" }}>
                    <h3 style={{ margin: "0 0 20px 0", color: "#495057" }}>Delete Lookup Record</h3>

                    <div style={{ marginBottom: "20px" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>Lookup Record ID:</label>
                        <input
                            type="number"
                            placeholder="Enter the ID of the record to delete"
                            value={deleteId}
                            onChange={(e) => setDeleteId(e.target.value)}
                            style={{ width: "100%", padding: "8px", border: "1px solid #ced4da", borderRadius: "4px" }}
                        />
                    </div>

                    <button
                        onClick={handleDelete}
                        disabled={deleteLoading || !deleteId}
                        style={{
                            padding: "12px 24px",
                            backgroundColor: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: deleteLoading || !deleteId ? "not-allowed" : "pointer",
                            fontSize: "1rem",
                            fontWeight: "500",
                            opacity: deleteLoading || !deleteId ? 0.6 : 1,
                            width: "100%",
                        }}
                    >
                        {deleteLoading ? (
                            <>
                                <span className="loading-spinner" />
                                Deleting...
                            </>
                        ) : (
                            "Delete Record"
                        )}
                    </button>

                    <div
                        style={{
                            marginTop: "15px",
                            fontSize: "0.9rem",
                            color: "#856404",
                            backgroundColor: "#fff3cd",
                            padding: "10px",
                            borderRadius: "4px",
                            border: "1px solid #ffeaa7",
                        }}
                    >
                        <strong>Warning:</strong> This action permanently deletes the lookup record and cannot be undone.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LookupManagementPanel;
